import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

// Modal that collects one missing scalar field for one or more tasks before
// they are created. Used for style_no by default, and reused (via inputType)
// for CAD due date when orders are missing a deadline.
//   items: [{ key: string, label: string }]
//   inputType / inputPlaceholder / description: customizable per caller.
//   onSave(values): object keyed by item.key with the entered value.
//   onCancel(): parent decides whether to abort the create/import.
// All inputs required — Save stays disabled until every value is filled.
export function StyleNoPromptDialog({
  open,
  items,
  onSave,
  onCancel,
  title,
  description,
  inputType = 'text',
  inputPlaceholder = 'e.g. KSR-1234',
  saveLabel = 'Save & Continue',
}) {
  const [values, setValues] = useState({});

  useEffect(() => {
    if (open) {
      const initial = {};
      items?.forEach(item => { initial[item.key] = ''; });
      setValues(initial);
    }
  }, [open, items]);

  const allFilled = items?.length > 0 && items.every(item => (values[item.key] || '').trim());

  const handleSave = () => {
    if (!allFilled) return;
    const trimmed = {};
    for (const k of Object.keys(values)) trimmed[k] = values[k].trim();
    onSave(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-amber-500" size={18} />
            {title || 'Style No required'}
          </DialogTitle>
          <DialogDescription>
            {description || 'Style No is needed to name and identify each piece. Enter one for each item below.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
          {items?.map((item, idx) => (
            <div key={item.key}>
              <Label className="text-xs">{item.label}</Label>
              <Input
                type={inputType}
                value={values[item.key] || ''}
                onChange={e => setValues(prev => ({ ...prev, [item.key]: e.target.value }))}
                placeholder={inputPlaceholder}
                className="h-9 mt-1"
                autoFocus={idx === 0}
                onKeyDown={e => { if (e.key === 'Enter' && allFilled) handleSave(); }}
                data-testid={`style-no-prompt-input-${item.key}`}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            disabled={!allFilled}
            onClick={handleSave}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="style-no-prompt-save"
          >
            {saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
